import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  PhotoCamera,
  Upload,
  ArrowForward,
  ArrowBack
} from '@mui/icons-material';
import { BrowserMultiFormatReader } from '@zxing/browser';

interface ProductPhotoCaptureProps {
  open: boolean;
  onClose: () => void;
  onComplete: (ingredientsPhoto: string, nutritionalPhoto: string) => void;
  barcode: string;
}

const ProductPhotoCapture: React.FC<ProductPhotoCaptureProps> = ({ 
  open, 
  onClose, 
  onComplete, 
  barcode 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [ingredientsPhoto, setIngredientsPhoto] = useState<string>('');
  const [nutritionalPhoto, setNutritionalPhoto] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const steps = [
    'Fotografiază Lista de Ingrediente',
    'Fotografiază Informațiile Nutriționale',
    'Verifică și Trimite'
  ];

  const handleClose = () => {
    setActiveStep(0);
    setIngredientsPhoto('');
    setNutritionalPhoto('');
    setError(null);
    onClose();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Te rugăm să selectezi un fișier imagine (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Fișierul este prea mare. Te rugăm să selectezi o imagine mai mică de 10MB.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        const base64 = result.split(',')[1]; // Remove data:image/...;base64, prefix
        
        if (activeStep === 0) {
          setIngredientsPhoto(base64);
        } else {
          setNutritionalPhoto(base64);
        }
        setIsProcessing(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Eroare la procesarea imaginii. Te rugăm să încerci din nou.');
      setIsProcessing(false);
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCameraClick = () => {
    if (fileInputRef.current && !isProcessing) {
      fileInputRef.current.click();
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !ingredientsPhoto) {
      setError('Te rugăm să fotografiezi mai întâi lista de ingrediente');
      return;
    }
    if (activeStep === 1 && !nutritionalPhoto) {
      setError('Te rugăm să fotografiezi mai întâi informațiile nutriționale');
      return;
    }
    setError(null);
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = async () => {
    if (!ingredientsPhoto || !nutritionalPhoto) {
      setError('Ambele fotografii sunt obligatorii');
      return;
    }
    
    setIsAnalyzing(true);
    setError(null);
    
    try {
      await onComplete(ingredientsPhoto, nutritionalPhoto);
      handleClose();
    } catch (error) {
      setError('Eroare la analizarea fotografiilor. Te rugăm să încerci din nou.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', minHeight: 400 }}>
            <Typography variant="h6" gutterBottom>
              Pasul 1: Fotografiază Lista de Ingrediente
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Fă o fotografie clară a listei de ingrediente de pe ambalajul produsului
            </Typography>
            
            {ingredientsPhoto ? (
              <Box>
                <img 
                  src={`data:image/jpeg;base64,${ingredientsPhoto}`} 
                  alt="Ingredients" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: 300, 
                    borderRadius: 8,
                    border: '2px solid #1976d2'
                  }} 
                />
                <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                  ✓ Fotografia cu ingredientele a fost capturată cu succes
                </Typography>
              </Box>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease'
                }}
              >
                <Upload sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Încarcă Fotografia cu Ingredientele
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Selectează o fotografie clară a listei de ingrediente
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PhotoCamera />}
                  onClick={handleCameraClick}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Se procesează...' : 'Alege Fotografia'}
                </Button>
              </Paper>
            )}
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ textAlign: 'center', minHeight: 400 }}>
            <Typography variant="h6" gutterBottom>
              Pasul 2: Fotografiază Informațiile Nutriționale
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Fă o fotografie clară a tabelului cu informațiile nutriționale de pe ambalajul produsului
            </Typography>
            
            {nutritionalPhoto ? (
              <Box>
                <img 
                  src={`data:image/jpeg;base64,${nutritionalPhoto}`} 
                  alt="Nutritional Information" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: 300, 
                    borderRadius: 8,
                    border: '2px solid #1976d2'
                  }} 
                />
                <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                  ✓ Fotografia cu informațiile nutriționale a fost capturată cu succes
                </Typography>
              </Box>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease'
                }}
              >
                <Upload sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Încarcă Fotografia cu Informațiile Nutriționale
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Selectează o fotografie clară a tabelului cu informațiile nutriționale
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PhotoCamera />}
                  onClick={handleCameraClick}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Se procesează...' : 'Alege Fotografia'}
                </Button>
              </Paper>
            )}
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ textAlign: 'center', minHeight: 400, position: 'relative' }}>
            {isAnalyzing ? (
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Se analizează fotografiile...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  IA extrage ingredientele și informațiile nutriționale
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Acest proces poate dura 30-60 de secunde
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>
                  Verifică Fotografiile
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Verifică fotografiile înainte de a le trimite pentru analiză
                </Typography>
                
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Fotografia cu Ingrediente:
                    </Typography>
                    <img 
                      src={`data:image/jpeg;base64,${ingredientsPhoto}`} 
                      alt="Ingredients" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: 200, 
                        borderRadius: 8,
                        border: '1px solid #ddd'
                      }} 
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Fotografia cu Informațiile Nutriționale:
                    </Typography>
                    <img 
                      src={`data:image/jpeg;base64,${nutritionalPhoto}`} 
                      alt="Nutritional Information" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: 200, 
                        borderRadius: 8,
                        border: '1px solid #ddd'
                      }} 
                    />
                  </Box>
                </Stack>
              </>
            )}
          </Box>
        );
      
      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          Analiza Produsului - {barcode}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {getStepContent(activeStep)}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Anulează
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<ArrowBack />}>
            Înapoi
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext} 
            variant="contained" 
            endIcon={<ArrowForward />}
            disabled={
              (activeStep === 0 && !ingredientsPhoto) ||
              (activeStep === 1 && !nutritionalPhoto)
            }
          >
            Următorul
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="success"
            disabled={!ingredientsPhoto || !nutritionalPhoto || isAnalyzing}
            startIcon={isAnalyzing ? <CircularProgress size={20} /> : null}
          >
            {isAnalyzing ? 'Se analizează...' : 'Trimite pentru Analiză'}
          </Button>
        )}
      </DialogActions>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
    </Dialog>
  );
};

export default ProductPhotoCapture;
